"use client";

import { ArrowLeft, BookOpen, DownloadSimple, Fire, MagnifyingGlass, ShareNetwork } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useMemo } from "react";
import { Navbar, Footer } from "@/components/home";
import { useTranslations } from "@/i18n";
import {
  getWisdomTranslationOption,
  getWisdomTranslationText,
  hasWisdomTranslation,
  WISDOM_TRANSLATION_GROUPS,
  type WisdomScriptureText,
  type WisdomTranslationCode,
} from "@/lib/wisdomTranslations";

// Types matching the API response
type ScriptureTranslation = WisdomScriptureText;

export interface WisdomEntry {
  day: number;
  date: string;
  imageUrl: string;
  scripture: string;
  scriptures: ScriptureTranslation[];
}

interface WisdomAPIResponse {
  latestDay: number;
  totalDays: number;
  wisdoms: WisdomEntry[];
}

export function WisdomArchiveClient({ initialWisdoms }: { initialWisdoms: WisdomEntry[] }) {
  const dictionary = useTranslations();
  const t = dictionary.wisdomArchive;
  const [wisdoms, setWisdoms] = useState<WisdomEntry[]>(initialWisdoms);
  const [selectedWisdom, setSelectedWisdom] = useState<WisdomEntry | null>(null);
  // Server-rendered data arrives via props; the client fetch below is only a
  // fallback for when the server-side fetch failed at build/revalidate time
  const [isLoading, setIsLoading] = useState(initialWisdoms.length === 0);
  const [scriptureVersion, setScriptureVersion] = useState<WisdomTranslationCode>('kjv');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(24);

  // Fallback fetch, used only when no server data was provided
  useEffect(() => {
    if (initialWisdoms.length > 0) return;
    const fetchWisdoms = async () => {
      try {
        const response = await fetch('https://winnerschapelcanada.ca/api/wisdom');
        if (response.ok) {
          const data: WisdomAPIResponse = await response.json();
          setWisdoms(data.wisdoms);
        }
      } catch (error) {
        console.error('Failed to fetch wisdom data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchWisdoms();
  }, [initialWisdoms.length]);

  // Filter wisdoms based on search
  const filteredWisdoms = useMemo(() => {
    if (!searchQuery.trim()) return wisdoms;
    const query = searchQuery.toLowerCase();
    return wisdoms.filter(
      (w) =>
        w.scripture.toLowerCase().includes(query) ||
        w.day.toString().includes(query) ||
        w.scriptures.some((s) => s.reference.toLowerCase().includes(query))
    );
  }, [wisdoms, searchQuery]);

  // Render the archive incrementally: mounting every day at once requested
  // 150+ images on load and pushed LCP past 9s on throttled connections
  const visibleWisdoms = useMemo(
    () => filteredWisdoms.slice(0, visibleCount),
    [filteredWisdoms, visibleCount]
  );

  const handleDownload = async (wisdom: WisdomEntry) => {
    try {
      const response = await fetch(wisdom.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `yaf-wisdom-day-${wisdom.day}.jpeg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  useEffect(() => {
    if (!selectedWisdom) return;

    const option = getWisdomTranslationOption(scriptureVersion);
    const isNewOptionalLanguage =
      option?.languageKey === "spanish" || option?.languageKey === "creole";

    if (
      isNewOptionalLanguage &&
      !hasWisdomTranslation(selectedWisdom.scriptures, scriptureVersion)
    ) {
      setScriptureVersion("kjv");
    }
  }, [scriptureVersion, selectedWisdom]);

  return (
    <main className="min-h-screen bg-black selection:bg-brand-red selection:text-white">
      <Navbar forceDark />

      {/* Hero Header */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-brand-red via-transparent to-transparent blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            {t.backToHome}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brand-gold text-sm tracking-[0.4em] uppercase mb-4 block font-black flex items-center gap-3">
              <Fire weight="fill" className="w-5 h-5" />
              {t.trajectory}
            </span>
            <h1 className="font-serif text-5xl lg:text-7xl font-bold text-white tracking-tighter mb-4">
              Scriptural Wisdom <span className="text-brand-red italic">Archive</span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl font-light">
              {t.description}
            </p>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 max-w-md"
          >
            <div className="relative">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-zinc-900/80 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold/50 transition-all"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Grid. min-h keeps the footer (and its background video) off-screen
          while data loads; without it the page collapses, the footer's
          IntersectionObserver fires, and ~4MB of video streams on page load */}
      <section className="pb-32 px-6 min-h-[100vh]">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredWisdoms.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-zinc-400 text-lg">{t.noResults.replace("{query}", searchQuery)}</p>
            </div>
          ) : (
            // Position-only entrances: opacity-gating the grid hid the LCP
            // image until hydration finished on throttled devices
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {visibleWisdoms.map((wisdom, index) => (
                <motion.div
                  key={wisdom.day}
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  // Cap the stagger: an uncapped index delay made late cards wait
                  // seconds to appear, inflating LCP and failing contrast audits
                  // that sample colors mid-fade
                  transition={{ duration: 0.4, delay: Math.min(index, 11) * 0.05 }}
                  className="group relative"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-zinc-900 cursor-pointer"
                    onClick={() => setSelectedWisdom(wisdom)}
                  >
                    <Image
                      src={wisdom.imageUrl}
                      alt={`Day ${wisdom.day} - ${wisdom.scripture}`}
                      fill
                      sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      // First two rows are above the fold; lazy-loading them
                      // delays LCP since the top-left card is the LCP element.
                      // fetchPriority (not the Next 16 deprecated `priority`)
                      fetchPriority={index < 4 ? "high" : "auto"}
                      loading={index < 8 ? "eager" : "lazy"}
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Day badge */}
                    <div className="absolute top-3 left-3 bg-brand-red text-white px-3 py-1.5 rounded-lg font-black text-xs flex items-center gap-1.5">
                      <Fire weight="fill" className="w-3 h-3" />
                      Day {wisdom.day}
                    </div>

                    {/* Hover actions */}
                    <div className="absolute bottom-3 left-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(wisdom);
                        }}
                        className="flex-1 bg-white/90 backdrop-blur-sm text-zinc-900 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-white transition-colors"
                      >
                        <DownloadSimple weight="bold" className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>

                  {/* Scripture reference below */}
                  {/* zinc-400 (not 500): 12px text on black needs 4.5:1 contrast; zinc-500 is 4.35:1 and fails WCAG AA */}
                  <p className="mt-2 text-xs text-zinc-400 truncate font-medium">
                    {wisdom.scripture}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          {!isLoading && filteredWisdoms.length > visibleCount && (
            <div className="flex justify-center mt-12">
              <button
                onClick={() => setVisibleCount((c) => c + 24)}
                className="px-10 py-4 rounded-full border border-white/20 text-white font-display font-bold text-sm uppercase tracking-widest hover:bg-white/10 hover:border-white/40 transition-colors"
              >
                Load More Days ({filteredWisdoms.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedWisdom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setSelectedWisdom(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="bg-zinc-900 rounded-2xl overflow-hidden max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid md:grid-cols-2">
                {/* Image */}
                <div className="relative aspect-[3/4]">
                  <Image
                    src={selectedWisdom.imageUrl}
                    alt={`Day ${selectedWisdom.day}`}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-brand-red text-white px-4 py-2 rounded-xl font-black flex items-center gap-2">
                    <Fire weight="fill" className="w-4 h-4" />
                    Day {selectedWisdom.day}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 lg:p-8 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="font-serif text-2xl lg:text-3xl font-bold text-white mb-1">
                        Day {selectedWisdom.day}
                      </h2>
                      <p className="text-zinc-500 text-sm">
                        {new Date(selectedWisdom.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedWisdom(null)}
                      className="text-zinc-500 hover:text-white transition-colors p-2"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Version Toggle */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <BookOpen weight="fill" className="w-5 h-5 text-brand-gold" />
                      <span className="text-sm font-bold text-white">{selectedWisdom.scripture}</span>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {WISDOM_TRANSLATION_GROUPS.map((group) => (
                        <div key={group.languageKey}>
                          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-black mb-1">
                            {group.defaultLabel}
                          </p>
                          <div className="flex bg-black/50 rounded-lg p-1">
                            {group.options.map((option) => {
                              const isOptionalNewLanguage =
                                group.languageKey === "spanish" ||
                                group.languageKey === "creole";
                              const isUnavailable =
                                isOptionalNewLanguage &&
                                !hasWisdomTranslation(selectedWisdom.scriptures, option.code);

                              return (
                                <button
                                  key={option.code}
                                  onClick={() => setScriptureVersion(option.code)}
                                  disabled={isUnavailable}
                                  className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-all ${
                                    scriptureVersion === option.code ? 'bg-brand-gold text-black' : 'text-zinc-500 hover:text-white'
                                  } ${isUnavailable ? 'cursor-not-allowed opacity-40 hover:text-zinc-500' : ''}`}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Scripture Text */}
                  <div className="flex-1 overflow-y-auto space-y-4 mb-6">
                    {selectedWisdom.scriptures.map((scripture, index) => {
                      const text = getWisdomTranslationText(scripture, scriptureVersion);
                      const noTranslation = !text;

                      return (
                        <div key={index}>
                          <p className="text-xs font-black text-brand-gold uppercase tracking-wide mb-2">
                            {scripture.reference}
                          </p>
                          {noTranslation ? (
                            <p className="text-zinc-500 italic">
                              {dictionary.wisdomSection.noTranslation}
                            </p>
                          ) : (
                            <p className="text-zinc-300 leading-relaxed">
                              {text}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleDownload(selectedWisdom)}
                      className="flex-1 bg-brand-gold text-black py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-brand-gold/90 transition-colors"
                    >
                      <DownloadSimple weight="bold" className="w-5 h-5" />
                      Download
                    </button>
                    <button
                      onClick={async () => {
                        if (navigator.share) {
                          try {
                            await navigator.share({
                              title: `Scriptural Wisdom - Day ${selectedWisdom.day}`,
                              text: `Day ${selectedWisdom.day} - ${selectedWisdom.scripture}`,
                              url: window.location.href,
                            });
                          } catch {}
                        }
                      }}
                      className="bg-white/10 text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
                    >
                      <ShareNetwork weight="bold" className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </main>
  );
}
